/**
 * Admin scoreboard for surfacer versions ("have you missed X" report matching).
 *
 * Each report-matching version (prompt / LLM / cutoffs) is replayed over the
 * same frozen image predictions and reports and lands as its own replay cohort;
 * together they form a family. Verdicts are pooled across the family (a verdict
 * is about the study, finding and report, not the prompt), and each version is
 * scored against them. Addressed by any cohort id in the family, or the family's
 * predictions cohort. Routed and gated like the feedback overview (`AdminRoute`);
 * the backend enforces admin too, since the body pools every reviewer's verdicts.
 */

import { Info } from "lucide-react";
import { useState, type FormEvent, type ReactNode } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { Button, Input } from "@/design-system/components/react";

import {
  ScoreboardNotFoundError,
  useSurfacerScoreboard,
  type ScoreboardVersion,
  type SurfacerScoreboard as Scoreboard,
} from "../hooks/useSurfacerScoreboard";
import { surfacerScoreboardPath } from "../utils/surfacerScoreboard";

/** Version label: the model tag(s), else the cohort id (shown under it anyway). */
export function versionLabel(version: ScoreboardVersion): string {
  return version.models.length ? version.models.join(", ") : version.cohort_id;
}

function SummaryCard({
  label,
  value,
  detail,
}: {
  label: string;
  value: ReactNode;
  detail: string;
}) {
  return (
    <div className="rounded-sharp border border-border bg-surface-1 p-4">
      <div className="text-xs font-medium uppercase tracking-wide text-fg-muted">{label}</div>
      <div className="mt-2 text-2xl font-semibold text-fg">{value}</div>
      <div className="mt-1 text-xs text-fg-faint">{detail}</div>
    </div>
  );
}

/** "a / b" cell with each half titled, for the paired columns. A value an older
 * backend doesn't send renders as "—", not 0, so a missing count never reads as none. */
function Pair({ values }: { values: [number | undefined, string][] }) {
  return (
    <span className="whitespace-nowrap">
      {values.map(([value, title], i) => (
        <span key={title}>
          {i > 0 && <span className="text-fg-faint"> / </span>}
          {value === undefined ? (
            <span title={`${title}: not reported by this backend`} className="text-fg-faint">
              —
            </span>
          ) : (
            <span title={title}>{value}</span>
          )}
        </span>
      ))}
    </span>
  );
}

function Sub({ children }: { children: ReactNode }) {
  return (
    <div className="mt-0.5 whitespace-nowrap text-[11px] font-normal text-fg-faint">{children}</div>
  );
}

export function ScoreboardTable({
  versions,
  requestedCohortId,
}: {
  versions: ScoreboardVersion[];
  requestedCohortId: string;
}) {
  if (versions.length === 0) {
    return (
      <div className="rounded-sharp border border-dashed border-border-strong p-8 text-center text-sm text-fg-muted">
        No version in this family has report comparisons yet.
      </div>
    );
  }
  return (
    <div className="overflow-x-auto rounded-sharp border border-border bg-surface-1">
      <table className="w-full min-w-[1200px] text-sm" data-testid="surfacer-scoreboard-table">
        <thead className="bg-surface-2 text-xs uppercase tracking-wide text-fg-muted">
          <tr>
            <th className="px-4 py-3 text-left font-medium">Version</th>
            <th className="px-3 py-3 text-right font-medium">Studies</th>
            <th className="px-3 py-3 text-right font-medium">Shown</th>
            <th className="px-3 py-3 text-right font-medium">True misses kept (billable)</th>
            <th className="px-3 py-3 text-right font-medium">True misses hidden as reported</th>
            <th className="px-3 py-3 text-right font-medium">
              Report mentions hidden / wrongly shown
            </th>
            <th className="px-3 py-3 text-right font-medium">
              In other report (shown / not covered / hidden)
            </th>
            <th className="px-3 py-3 text-right font-medium">
              Overcall / Wrong name / Inconsequential shown
            </th>
            <th className="px-3 py-3 text-right font-medium">Could be right</th>
            <th className="px-3 py-3 text-right font-medium">Unjudged (shown / hidden)</th>
          </tr>
        </thead>
        <tbody>
          {versions.map((v) => (
            <tr
              key={v.cohort_id}
              className="border-t border-border align-top hover:bg-surface-2/60"
              data-testid={`scoreboard-row-${v.cohort_id}`}
            >
              <td className="px-4 py-3">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-medium text-fg">{versionLabel(v)}</span>
                  {v.cohort_id === requestedCohortId && (
                    <span className="rounded-sharp border border-signal-stroke bg-signal-tint px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-signal-text-tint">
                      Opened from
                    </span>
                  )}
                </div>
                <div className="mt-0.5 font-mono text-[10px] text-fg-faint">{v.cohort_id}</div>
              </td>
              <td className="px-3 py-3 text-right text-fg-muted">{v.studies}</td>
              <td className="px-3 py-3 text-right text-fg">
                {v.candidates_shown}
                <Sub>
                  {v.surfaced} surfaced · {v.candidates_shown - v.surfaced} sunk
                </Sub>
              </td>
              <td className="px-3 py-3 text-right font-medium text-fg">
                {v.true_miss_kept_billable}
                {v.true_miss_kept !== v.true_miss_kept_billable && (
                  <Sub>of {v.true_miss_kept} kept</Sub>
                )}
              </td>
              <td className="px-3 py-3 text-right text-fg">
                {v.true_miss_hidden}
                <Sub>of {v.hidden_reported} hidden</Sub>
                {v.true_miss_sunk_no_coverage > 0 && (
                  <Sub>+{v.true_miss_sunk_no_coverage} sunk as not covered</Sub>
                )}
              </td>
              <td className="px-3 py-3 text-right text-fg">
                <Pair
                  values={[
                    [v.report_mentions_hidden, "Report mentions, correctly hidden"],
                    [v.report_mentions_shown, "Report mentions, wrongly shown"],
                  ]}
                />
              </td>
              <td className="px-3 py-3 text-right text-fg">
                <Pair
                  values={[
                    [v.other_report_shown, "In other report, surfaced"],
                    [v.other_report_sunk_no_coverage, "In other report, sunk as not covered"],
                    [v.other_report_hidden, "In other report, hidden as reported"],
                  ]}
                />
              </td>
              <td className="px-3 py-3 text-right text-fg">
                <Pair
                  values={[
                    [v.overcall_shown, "Overcall, shown"],
                    [v.wrong_name_shown, "Wrong name, shown"],
                    [v.inconsequential_shown, "Inconsequential, shown"],
                  ]}
                />
              </td>
              <td className="px-3 py-3 text-right text-fg">{v.ambiguous}</td>
              <td className="px-3 py-3 text-right text-fg">
                <Pair
                  values={[
                    [v.unjudged_shown, "Unjudged, shown"],
                    [v.unjudged_hidden, "Unjudged, hidden as reported"],
                  ]}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Definitions, restated from web-demo-backend `src/surfacer_scoreboard.py`
 * (module docstring + `score_version`) and `src/surfacer_family.py`. Keep in step. */
const DEFINITIONS: [string, string][] = [
  [
    "Version",
    "One replay cohort: the same image predictions and reports as the rest of the family, with different report matching (prompt, model or cutoffs).",
  ],
  [
    "Label",
    "The most recent verdict any reviewer gave a study+finding, in any version of the family. Every verdict counts for every version, whichever version it was given under. Pending (cleared) verdicts don't count.",
  ],
  [
    "Surfaced",
    "The version shows the finding as a likely miss: the AI fired at the recommended operating point or stricter on a billable tier, the report doesn't state it, and the adjudicator did not predict it as noise.",
  ],
  [
    "Hidden as reported",
    "The version says the report already states the finding: either it is in the matched section, or it is a candidate the adjudicator predicted the report mentions (sunk below the divider in the panel).",
  ],
  [
    "Not covered",
    "A candidate the adjudicator predicted the report doesn't cover (that region isn't in the report). Sunk, but not a claim that the report mentions it, so it counts as neither kept nor hidden.",
  ],
  ["Studies", "Studies in the version that have a report comparison."],
  [
    "Shown",
    "Every card the panel lists, sunk ones included. Under it: how many were surfaced, and how many sunk below the divider (predicted already reported, or not covered).",
  ],
  [
    "True misses kept (billable)",
    "Surfaced and labelled True miss, on a billable clinical tier (acute or prompt workup). Both filters already require that tier, so today this equals all true misses kept; it is counted from each finding so it stays right if the filter widens.",
  ],
  [
    "True misses hidden as reported",
    "Hidden as reported, but labelled True miss: real misses the version filed as already in the report. True misses sunk as not covered are counted separately.",
  ],
  [
    "Report mentions hidden / wrongly shown",
    "Labelled Report mentions and hidden as reported (correct), or surfaced (wrongly shown).",
  ],
  [
    "In other report (shown / not covered / hidden)",
    "Labelled In other report: the report sends that region to a separate report (e.g. 'see CT chest report'), so the finding can't be judged as missed. Counted where the version put it: surfaced, sunk as not covered, or hidden as reported. Never counted as a true miss, a report mention or a false flag, but the finding is judged, so it is not unjudged. A dash means the backend predates this verdict.",
  ],
  [
    "Overcall / Wrong name / Inconsequential shown",
    "Surfaced and labelled with that verdict: findings the version should not have shown.",
  ],
  ["Could be right", "Labelled Could be right, wherever the version put it."],
  [
    "Unjudged (shown / hidden)",
    "Shown: cards with no label, the review this version still needs. Hidden: findings hidden as reported with no label, suppressions nobody has checked (a hidden finding only gets a label from a version that showed it).",
  ],
  [
    "Conflicting pairs",
    "Study+findings where reviewers gave different verdicts. The latest verdict is the label.",
  ],
];

function HowCounted() {
  return (
    <details className="group rounded-sharp border border-border bg-surface-1 px-4 py-3 text-sm">
      <summary className="cursor-pointer select-none font-medium text-fg">
        How these are counted
      </summary>
      <p className="mt-3 text-xs leading-5 text-fg-muted">
        Every count is over study+finding pairs in that version&apos;s studies. Each billable
        finding a version handles lands in exactly one place: surfaced, hidden as reported, or sunk
        as not covered.
      </p>
      <dl className="mt-3 grid gap-x-6 gap-y-2 text-xs leading-5 md:grid-cols-[220px_1fr]">
        {DEFINITIONS.map(([term, definition]) => (
          <div key={term} className="contents">
            <dt className="font-medium text-fg">{term}</dt>
            <dd className="text-fg-muted">{definition}</dd>
          </div>
        ))}
      </dl>
    </details>
  );
}

export function ScoreboardBody({ board }: { board: Scoreboard }) {
  return (
    <div className="space-y-5">
      <div className="grid gap-3 sm:grid-cols-3">
        <SummaryCard
          label="Judged pairs"
          value={board.pooled.judged_pairs}
          detail="Study+findings with a verdict, pooled across every version"
        />
        <SummaryCard
          label="Reviewers"
          value={board.pooled.reviewers}
          detail="Gave at least one of those verdicts"
        />
        <SummaryCard
          label="Conflicting pairs"
          value={board.pooled.conflicting_pairs}
          detail="Reviewers disagree"
        />
      </div>
      <div className="flex items-start gap-2 rounded-sharp border border-border bg-surface-1 px-4 py-3 text-xs leading-5 text-fg-muted">
        <Info className="mt-0.5 size-4 shrink-0 text-signal-text-tint" aria-hidden="true" />
        <span>
          When reviewers disagree on a finding, the latest verdict counts. Verdicts carry no
          environment marker, so staging and production verdicts are pooled together.
        </span>
      </div>

      <ScoreboardTable versions={board.versions} requestedCohortId={board.requested_cohort_id} />

      <dl className="grid gap-x-6 gap-y-1 text-xs text-fg-muted sm:grid-cols-2">
        <div>
          <dt className="inline text-fg-faint">Predictions cohort: </dt>
          <dd className="inline font-mono text-[11px]">
            {board.source_cohort_id ?? "unknown (this cohort alone)"}
          </dd>
        </div>
        {board.non_version_cohort_ids.length > 0 && (
          <div>
            <dt className="inline text-fg-faint">In the family, no report comparisons: </dt>
            <dd className="inline font-mono text-[11px]">
              {board.non_version_cohort_ids.join(", ")}
            </dd>
          </div>
        )}
      </dl>

      <HowCounted />
    </div>
  );
}

function CohortForm({ initial }: { initial: string }) {
  const navigate = useNavigate();
  const [value, setValue] = useState(initial);
  const submit = (event: FormEvent) => {
    event.preventDefault();
    const cohortId = value.trim();
    if (cohortId) navigate(surfacerScoreboardPath(cohortId));
  };
  return (
    <form onSubmit={submit} className="flex flex-wrap items-end gap-2">
      <label className="flex min-w-72 flex-col gap-1 text-xs text-fg-muted">
        <span>Cohort id (any version, or the predictions cohort)</span>
        <Input
          value={value}
          onChange={(event) => setValue(event.target.value)}
          className="bg-surface font-mono"
        />
      </label>
      <Button type="submit" size="sm" variant="outline">
        Show
      </Button>
    </form>
  );
}

export default function SurfacerScoreboard() {
  const { cohortId } = useParams<{ cohortId?: string }>();
  const { data, isLoading, error } = useSurfacerScoreboard(cohortId);

  return (
    <div className="mx-auto w-full max-w-[1440px] space-y-6 p-4 text-fg sm:p-6">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">Surfacer version scoreboard</h1>
          <p className="mt-1 max-w-3xl text-sm text-fg-muted">
            Every report-matching version of one cohort, scored against the verdicts reviewers gave
            in any of them.
          </p>
        </div>
        <CohortForm key={cohortId ?? ""} initial={cohortId ?? ""} />
      </header>

      {!cohortId ? (
        <div className="text-sm text-fg-muted">Enter a cohort id to see its versions.</div>
      ) : isLoading ? (
        <div className="text-fg-muted">Loading the scoreboard…</div>
      ) : error instanceof ScoreboardNotFoundError ? (
        <div className="text-fg-muted">No versions found for cohort {cohortId}.</div>
      ) : error ? (
        <div className="text-warn">Failed to load the scoreboard.</div>
      ) : data ? (
        <ScoreboardBody board={data} />
      ) : null}
    </div>
  );
}
